import { PolarstepsLocationPoint, PolarstepsStep } from '../types/polarsteps';
import { MatchingConfidence } from '../types/domain';

export interface InterpolationOptions {
  maxGapSeconds?: number; // Default: 1800 (30 minutes)
  snapThresholdSeconds?: number; // Default: 600 (10 minutes)
  stepToleranceSeconds?: number; // Default: 3600 (1 hour)
  fallbackToNearest?: boolean; // When true: guarantees 100% geotagging by snapping unmatched photos to nearest fix or step
}

export interface GpsMatchResult {
  lat: number | null;
  lon: number | null;
  matchingConfidence: MatchingConfidence;
  source:
    | 'exact_fix'
    | 'interpolated'
    | 'snap_first'
    | 'snap_last'
    | 'snap_gap'
    | 'step_fallback'
    | 'unmatched';
  stepName?: string;
  gapDurationSeconds?: number;
}

/**
 * Snaps an unmatched photo to the temporally closest GPS fix or Step check-in across the entire trip.
 */
function snapToNearestFix(
  photoTimeUtc: number,
  track: PolarstepsLocationPoint[],
  steps: PolarstepsStep[]
): GpsMatchResult {
  let nearestStep: PolarstepsStep | null = null;
  let minStepDiff = Infinity;
  for (const s of steps) {
    const diff = Math.abs(photoTimeUtc - s.start_time);
    if (diff < minStepDiff) {
      minStepDiff = diff;
      nearestStep = s;
    }
  }

  let nearestPoint: PolarstepsLocationPoint | null = null;
  let minPointDiff = Infinity;
  if (track && track.length > 0) {
    const first = track[0];
    const last = track[track.length - 1];
    if (photoTimeUtc <= first.time) {
      nearestPoint = first;
      minPointDiff = first.time - photoTimeUtc;
    } else if (photoTimeUtc >= last.time) {
      nearestPoint = last;
      minPointDiff = photoTimeUtc - last.time;
    } else {
      const idx = findTrackSegment(track, photoTimeUtc);
      if (idx >= 0 && idx < track.length - 1) {
        const p1 = track[idx];
        const p2 = track[idx + 1];
        const d1 = Math.abs(photoTimeUtc - p1.time);
        const d2 = Math.abs(photoTimeUtc - p2.time);
        if (d1 < d2) {
          nearestPoint = p1;
          minPointDiff = d1;
        } else {
          nearestPoint = p2;
          minPointDiff = d2;
        }
      }
    }
  }

  if (nearestStep && minStepDiff <= minPointDiff) {
    return {
      lat: nearestStep.location.lat,
      lon: nearestStep.location.lon,
      matchingConfidence: 'step_fallback',
      source: 'step_fallback',
      stepName: nearestStep.name,
    };
  }

  if (nearestPoint) {
    return {
      lat: nearestPoint.lat,
      lon: nearestPoint.lon,
      matchingConfidence: 'snap_fallback',
      source: 'snap_gap',
    };
  }

  if (nearestStep) {
    return {
      lat: nearestStep.location.lat,
      lon: nearestStep.location.lon,
      matchingConfidence: 'step_fallback',
      source: 'step_fallback',
      stepName: nearestStep.name,
    };
  }

  return {
    lat: null,
    lon: null,
    matchingConfidence: 'unmatched',
    source: 'unmatched',
  };
}

/**
 * Normalizes an angle into the [-180, +180] degree interval.
 */
export function normalizeLongitude(lon: number): number {
  return ((lon + 180) % 360 + 360) % 360 - 180;
}

/**
 * Calculates shortest signed longitudinal angular delta across the anti-meridian.
 */
export function shortestLongitudeDelta(lon1: number, lon2: number): number {
  let delta = lon2 - lon1;
  while (delta > 180) delta -= 360;
  while (delta < -180) delta += 360;
  return delta;
}

/**
 * Binary search on chronologically sorted track points.
 * Returns the index i such that track[i].time <= targetTime < track[i+1].time.
 * Returns -1 if targetTime < track[0].time.
 * Returns track.length - 1 if targetTime >= track[track.length - 1].time.
 * Complexity: O(log N).
 */
export function findTrackSegment(track: PolarstepsLocationPoint[], targetTime: number): number {
  if (!track || track.length === 0) return -1;
  if (targetTime < track[0].time) return -1;
  if (targetTime >= track[track.length - 1].time) return track.length - 1;

  let low = 0;
  let high = track.length - 1;
  let result = 0;

  while (low <= high) {
    const mid = (low + high) >> 1;
    if (track[mid].time <= targetTime) {
      result = mid;
      low = mid + 1; // Seek further right
    } else {
      high = mid - 1;
    }
  }

  return result;
}

// Alias for findTrackSegment
export const findTrackSegmentIndex = findTrackSegment;

/**
 * Matches a timestamp against Polarsteps steps.
 */
export function matchAgainstSteps(
  photoTimeUtc: number,
  steps: PolarstepsStep[],
  toleranceSeconds = 3600
): GpsMatchResult {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const start = step.start_time - toleranceSeconds;
    const nextStart = i < steps.length - 1 ? steps[i + 1].start_time : undefined;
    const end = (step.end_time || nextStart || step.start_time + 86400) + toleranceSeconds;

    if (photoTimeUtc >= start && photoTimeUtc <= end) {
      return {
        lat: step.location.lat,
        lon: step.location.lon,
        matchingConfidence: 'step_fallback',
        source: 'step_fallback',
        stepName: step.name,
      };
    }
  }

  return {
    lat: null,
    lon: null,
    matchingConfidence: 'unmatched',
    source: 'unmatched',
  };
}

/**
 * Core GPS Interpolation Engine
 * Executes O(log N) binary search with anti-meridian wrapping, 30-min gap cutoff,
 * endpoint snapping, and step fallback.
 */
export function interpolateGpsLocation(
  photoTimeUtc: number,
  track: PolarstepsLocationPoint[],
  steps: PolarstepsStep[] = [],
  options: InterpolationOptions = {}
): GpsMatchResult {
  const result = evaluateGpsLocation(photoTimeUtc, track, steps, options);
  if (result.matchingConfidence === 'unmatched' && options.fallbackToNearest) {
    return snapToNearestFix(photoTimeUtc, track, steps);
  }
  return result;
}

function evaluateGpsLocation(
  photoTimeUtc: number,
  track: PolarstepsLocationPoint[],
  steps: PolarstepsStep[] = [],
  options: InterpolationOptions = {}
): GpsMatchResult {
  const maxGap = options.maxGapSeconds ?? 1800; // 30 minutes
  const snapThreshold = options.snapThresholdSeconds ?? 600; // 10 minutes
  const stepTolerance = options.stepToleranceSeconds ?? 3600; // 1 hour

  // 1. Boundary condition: Empty GPS track
  if (!track || track.length === 0) {
    return matchAgainstSteps(photoTimeUtc, steps, stepTolerance);
  }

  const firstPoint = track[0];
  const lastPoint = track[track.length - 1];

  // 2. Photo taken before the first GPS track point
  if (photoTimeUtc < firstPoint.time) {
    const diff = firstPoint.time - photoTimeUtc;
    if (diff <= snapThreshold) {
      return {
        lat: firstPoint.lat,
        lon: firstPoint.lon,
        matchingConfidence: diff < 1e-3 ? 'exact' : 'snap_fallback',
        source: 'snap_first',
      };
    }
    return matchAgainstSteps(photoTimeUtc, steps, stepTolerance);
  }

  // Exact match with first point
  if (Math.abs(photoTimeUtc - firstPoint.time) < 1e-3) {
    return {
      lat: firstPoint.lat,
      lon: firstPoint.lon,
      matchingConfidence: 'exact',
      source: 'exact_fix',
    };
  }

  // 3. Photo taken at or after the last GPS track point
  if (photoTimeUtc >= lastPoint.time) {
    const diff = photoTimeUtc - lastPoint.time;
    if (diff <= snapThreshold) {
      return {
        lat: lastPoint.lat,
        lon: lastPoint.lon,
        matchingConfidence: diff < 1e-3 ? 'exact' : 'snap_fallback',
        source: 'snap_last',
      };
    }
    return matchAgainstSteps(photoTimeUtc, steps, stepTolerance);
  }

  // 4. Binary Search for surrounding segment [p1, p2]
  const idx = findTrackSegment(track, photoTimeUtc);
  if (idx < 0 || idx >= track.length - 1) {
    return matchAgainstSteps(photoTimeUtc, steps, stepTolerance);
  }

  const p1 = track[idx];
  const p2 = track[idx + 1];
  const gap = p2.time - p1.time;

  // Exact timestamp match check
  if (Math.abs(photoTimeUtc - p1.time) < 1e-3) {
    return { lat: p1.lat, lon: p1.lon, matchingConfidence: 'exact', source: 'exact_fix' };
  }
  if (Math.abs(photoTimeUtc - p2.time) < 1e-3) {
    return { lat: p2.lat, lon: p2.lon, matchingConfidence: 'exact', source: 'exact_fix' };
  }

  // 5. Gap Threshold Cutoff Check (Flight / Ferry / Signal Loss)
  if (gap > maxGap) {
    // Check if within snap threshold of departure fix
    if (photoTimeUtc - p1.time <= snapThreshold) {
      return {
        lat: p1.lat,
        lon: p1.lon,
        matchingConfidence: 'snap_fallback',
        source: 'snap_gap',
        gapDurationSeconds: gap,
      };
    }
    // Check if within snap threshold of arrival fix
    if (p2.time - photoTimeUtc <= snapThreshold) {
      return {
        lat: p2.lat,
        lon: p2.lon,
        matchingConfidence: 'snap_fallback',
        source: 'snap_gap',
        gapDurationSeconds: gap,
      };
    }
    // Beyond snap threshold in a > 30 min gap: strictly refuse to interpolate a line!
    return matchAgainstSteps(photoTimeUtc, steps, stepTolerance);
  }

  // 6. Linear Geodesic Interpolation with Anti-Meridian Wrapping
  const alpha = (photoTimeUtc - p1.time) / gap;
  const lat = p1.lat + alpha * (p2.lat - p1.lat);

  const deltaLon = shortestLongitudeDelta(p1.lon, p2.lon);
  const rawLon = p1.lon + alpha * deltaLon;
  const lon = normalizeLongitude(rawLon);

  return {
    lat,
    lon,
    matchingConfidence: 'interpolated',
    source: 'interpolated',
  };
}
